import React, { useEffect, useRef } from "react";
import {
  canUsePinScrub,
  canUseCinematicMotion,
  gsap,
  ScrollTrigger,
} from "../../lib/motion";

const STEPS = [
  {
    id: "discover",
    label: "Discover",
    title: "Understand the goal",
    body: "We clarify your audience, must-haves, and success metrics so the build stays focused.",
  },
  {
    id: "design",
    label: "Design",
    title: "Shape the experience",
    body: "Wireframes and UI direction that feel on-brand — clear hierarchy before a line of code.",
  },
  {
    id: "build",
    label: "Build",
    title: "Ship solid product",
    body: "Modern web or mobile stack, clean APIs, and cloud-ready infra you can grow on.",
  },
  {
    id: "launch",
    label: "Launch",
    title: "Go live & support",
    body: "Deploy, handoff, and a clear path for iteration — so you keep winning after launch day.",
  },
];

const ProcessSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track || !canUseCinematicMotion()) return;

    const ctx = gsap.context(() => {
      gsap.from(section.querySelectorAll("[data-process-intro]"), {
        y: 32,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });

      const cards = gsap.utils.toArray<HTMLElement>(
        track.querySelectorAll("[data-process-step]")
      );

      if (!canUsePinScrub()) {
        gsap.from(cards, {
          y: 36,
          opacity: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: {
            trigger: track,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        });
        return;
      }

      gsap.set(cards, { opacity: 0.25, y: 24 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${cards.length * 420}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        },
      });

      cards.forEach((card, i) => {
        tl.to(
          card,
          { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
          i * 0.45
        );
        if (i < cards.length - 1) {
          tl.to(card, { opacity: 0.35, duration: 0.35 }, i * 0.45 + 0.45);
        }
      });
    }, section);

    return () => {
      ctx.revert();
      ScrollTrigger.getAll()
        .filter((t) => t.trigger === section || t.trigger === track)
        .forEach((t) => t.kill());
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="process"
      className="process-section relative py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full bg-teal-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-2">
        <div className="mb-10 md:mb-14 text-center md:text-left">
          <p
            data-process-intro
            className="text-cyan-400 font-mono text-sm tracking-widest uppercase mb-2"
          >
            How I work
          </p>
          <h2
            data-process-intro
            className="text-3xl md:text-4xl font-bold text-white mb-3"
          >
            From idea to launch
          </h2>
          <p data-process-intro className="text-white/70 max-w-xl mx-auto md:mx-0 md:text-lg">
            A clear process so clients always know what happens next — and when
            you get working software.
          </p>
        </div>

        <div
          ref={trackRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
        >
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              data-process-step
              className="process-card group relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 md:p-7 hover:border-cyan-400/40 transition-colors duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300 font-mono text-sm font-semibold">
                  0{index + 1}
                </span>
                <span className="text-cyan-400/90 font-medium tracking-wide">
                  {step.label}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {step.title}
              </h3>
              <p className="text-white/65 leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
